import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckSquare, FolderKanban, Target, Users, RefreshCw, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AddTaskDialog, AddProjectDialog, AddLeadDialog } from '../components/MyWorkCreateDialogs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_STATUSES = ['todo', 'in_progress', 'awaiting_feedback', 'done'];
const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
const LEAD_STATUSES = ['new', 'contacted', 'qualified', 'unqualified'];

const fmt = (s) => (s || '-').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusColor = (s) => ({
  todo: 'bg-slate-100 text-slate-700', in_progress: 'bg-blue-100 text-blue-700',
  awaiting_feedback: 'bg-amber-100 text-amber-700', done: 'bg-green-100 text-green-700',
  planning: 'bg-slate-100 text-slate-700', active: 'bg-blue-100 text-blue-700',
  on_hold: 'bg-amber-100 text-amber-700', completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700', new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700', qualified: 'bg-green-100 text-green-700',
  unqualified: 'bg-red-100 text-red-700',
}[s] || 'bg-slate-100 text-slate-700');

const StatusBadge = ({ status }) => (
  <Badge variant="secondary" className={`${statusColor(status)} font-medium`}>{fmt(status)}</Badge>
);

const StatusFilter = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-48" data-testid={testId}>
      <SelectValue placeholder="Filter by status" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Statuses</SelectItem>
      {options.map((s) => (
        <SelectItem key={s} value={s}>{fmt(s)}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const today = new Date().toISOString().split('T')[0];

const TasksTable = ({ tasks, showAssignee, testId }) => (
  <Table data-testid={testId}>
    <TableHeader>
      <TableRow>
        <TableHead>Task</TableHead>
        {showAssignee && <TableHead>Assigned To</TableHead>}
        <TableHead>Status</TableHead>
        <TableHead>Priority</TableHead>
        <TableHead>Due Date</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {tasks.length === 0 ? (
        <TableRow><TableCell colSpan={showAssignee ? 5 : 4} className="text-center text-muted-foreground py-8">No tasks found</TableCell></TableRow>
      ) : tasks.map((t) => (
        <TableRow key={t.id}>
          <TableCell className="font-medium">{t.name}</TableCell>
          {showAssignee && <TableCell>{t.assigned_to_name || t.assigned_to || '-'}</TableCell>}
          <TableCell><StatusBadge status={t.status} /></TableCell>
          <TableCell>{fmt(t.priority)}</TableCell>
          <TableCell>
            {t.due_date ? (
              <span className={t.due_date < today && t.status !== 'done' ? 'text-red-600 font-semibold' : ''}>
                {t.due_date}{t.due_date < today && t.status !== 'done' ? ' (overdue)' : ''}
              </span>
            ) : '-'}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ProjectsTable = ({ projects, testId }) => (
  <Table data-testid={testId}>
    <TableHeader>
      <TableRow>
        <TableHead>Project</TableHead>
        <TableHead>Client</TableHead>
        <TableHead>Manager</TableHead>
        <TableHead>Status</TableHead>
        <TableHead>End Date</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {projects.length === 0 ? (
        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No projects found</TableCell></TableRow>
      ) : projects.map((p) => (
        <TableRow key={p.id}>
          <TableCell className="font-medium">{p.name}</TableCell>
          <TableCell>{p.client_name || '-'}</TableCell>
          <TableCell>{p.manager_name || '-'}</TableCell>
          <TableCell><StatusBadge status={p.status} /></TableCell>
          <TableCell>{p.end_date || '-'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const LeadsTable = ({ leads, showAssignee, testId }) => (
  <Table data-testid={testId}>
    <TableHeader>
      <TableRow>
        <TableHead>Lead</TableHead>
        <TableHead>Company</TableHead>
        {showAssignee && <TableHead>Assigned To</TableHead>}
        <TableHead>Status</TableHead>
        <TableHead>Contact</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {leads.length === 0 ? (
        <TableRow><TableCell colSpan={showAssignee ? 5 : 4} className="text-center text-muted-foreground py-8">No leads found</TableCell></TableRow>
      ) : leads.map((l) => (
        <TableRow key={l.id}>
          <TableCell className="font-medium">{l.name}</TableCell>
          <TableCell>{l.company || '-'}</TableCell>
          {showAssignee && <TableCell>{l.assigned_to_name || '-'}</TableCell>}
          <TableCell><StatusBadge status={l.status} /></TableCell>
          <TableCell>{l.phone || l.email || '-'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const byStatus = (items, status) => (status === 'all' ? items : items.filter((i) => i.status === status));

export const MyWorkPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taskStatus, setTaskStatus] = useState('all');
  const [projectStatus, setProjectStatus] = useState('all');
  const [leadStatus, setLeadStatus] = useState('all');
  const [teamEntity, setTeamEntity] = useState('tasks');
  const [teamStatus, setTeamStatus] = useState('all');
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my-work`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load your work items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground animate-pulse">Loading your work...</div>;
  }
  if (!data) return null;

  const { my, team } = data;
  const refs = data.refs || { members: [], projects: [], clients: [] };
  const teamStatusOptions = teamEntity === 'tasks' ? TASK_STATUSES : teamEntity === 'projects' ? PROJECT_STATUSES : LEAD_STATUSES;

  return (
    <div className="space-y-6" data-testid="my-work-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">My Work</h1>
          <p className="text-muted-foreground">Your tasks, projects and leads in one place</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowAddTask(true)} data-testid="add-task-btn">
            <Plus className="h-4 w-4 mr-1" /> Task
          </Button>
          <Button size="sm" onClick={() => setShowAddProject(true)} data-testid="add-project-btn">
            <Plus className="h-4 w-4 mr-1" /> Project
          </Button>
          <Button size="sm" onClick={() => setShowAddLead(true)} data-testid="add-lead-btn">
            <Plus className="h-4 w-4 mr-1" /> Lead
          </Button>
          <button onClick={load} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 ml-2" data-testid="my-work-refresh">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <AddTaskDialog open={showAddTask} onOpenChange={setShowAddTask} refs={refs} onCreated={load} />
      <AddProjectDialog open={showAddProject} onOpenChange={setShowAddProject} refs={refs} onCreated={load} />
      <AddLeadDialog open={showAddLead} onOpenChange={setShowAddLead} refs={refs} onCreated={load} />

      <Tabs defaultValue="tasks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tasks" className="gap-2" data-testid="my-work-tab-tasks">
            <CheckSquare className="h-4 w-4" /> My Tasks ({my.tasks.length})
          </TabsTrigger>
          <TabsTrigger value="projects" className="gap-2" data-testid="my-work-tab-projects">
            <FolderKanban className="h-4 w-4" /> My Projects ({my.projects.length})
          </TabsTrigger>
          <TabsTrigger value="leads" className="gap-2" data-testid="my-work-tab-leads">
            <Target className="h-4 w-4" /> My Leads ({my.leads.length})
          </TabsTrigger>
          {team && (
            <TabsTrigger value="team" className="gap-2" data-testid="my-work-tab-team">
              <Users className="h-4 w-4" /> Team
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="tasks">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>My Tasks</CardTitle>
                <CardDescription>Tasks assigned to you or created by you</CardDescription>
              </div>
              <StatusFilter value={taskStatus} onChange={setTaskStatus} options={TASK_STATUSES} testId="my-tasks-status-filter" />
            </CardHeader>
            <CardContent>
              <TasksTable tasks={byStatus(my.tasks, taskStatus)} testId="my-tasks-table" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>My Projects</CardTitle>
                <CardDescription>Projects you manage, own or are a team member of</CardDescription>
              </div>
              <StatusFilter value={projectStatus} onChange={setProjectStatus} options={PROJECT_STATUSES} testId="my-projects-status-filter" />
            </CardHeader>
            <CardContent>
              <ProjectsTable projects={byStatus(my.projects, projectStatus)} testId="my-projects-table" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>My Leads</CardTitle>
                <CardDescription>Leads assigned to you or created by you</CardDescription>
              </div>
              <StatusFilter value={leadStatus} onChange={setLeadStatus} options={LEAD_STATUSES} testId="my-leads-status-filter" />
            </CardHeader>
            <CardContent>
              <LeadsTable leads={byStatus(my.leads, leadStatus)} testId="my-leads-table" />
            </CardContent>
          </Card>
        </TabsContent>

        {team && (
          <TabsContent value="team">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>Team {fmt(teamEntity)}</CardTitle>
                  <CardDescription>
                    {team.scope === 'all' ? 'All records across the organisation' : 'Records belonging to your team members'}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select value={teamEntity} onValueChange={(v) => { setTeamEntity(v); setTeamStatus('all'); }}>
                    <SelectTrigger className="w-36" data-testid="team-entity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tasks">Tasks ({team.tasks.length})</SelectItem>
                      <SelectItem value="projects">Projects ({team.projects.length})</SelectItem>
                      <SelectItem value="leads">Leads ({team.leads.length})</SelectItem>
                    </SelectContent>
                  </Select>
                  <StatusFilter value={teamStatus} onChange={setTeamStatus} options={teamStatusOptions} testId="team-status-filter" />
                </div>
              </CardHeader>
              <CardContent>
                {teamEntity === 'tasks' && <TasksTable tasks={byStatus(team.tasks, teamStatus)} showAssignee testId="team-tasks-table" />}
                {teamEntity === 'projects' && <ProjectsTable projects={byStatus(team.projects, teamStatus)} testId="team-projects-table" />}
                {teamEntity === 'leads' && <LeadsTable leads={byStatus(team.leads, teamStatus)} showAssignee testId="team-leads-table" />}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
